/**
 * GlobalState.contacts 切片（14.18a）：通讯录数据的主线程只读镜像。
 *
 * 三个字段独立加载（requests 场景 K/L）：book=进通讯录页拉（with_friends），
 * points=同页红点（friend/point），groupChats=切群聊 Tab 才拉（group/list）。
 * Worker WS 会话期缓存命中零 HTTP；变更走 'chat/contacts' 推送**字段级合并**
 * （带哪个字段替换哪个，null=失效清空——通讯录页打开中发现 null 自动重 load，
 * UI effect「null 则 load」即是重拉口径，14.18b 接线）。登出全清。
 */

import type { GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type {
  AddFriendInput,
  AnnouncementItem,
  ApplyKind,
  BlackListUser,
  ContactApply,
  ContactGroupChat,
  ContactPoints,
  ContactsBook,
  ConversationKey,
  FriendChangeField,
  FriendProfile,
  GroupApplyInput,
  GroupChangeField,
  GroupMember,
  GroupMemberLogItem,
  InviteMembersResult,
  GroupProfileSnapshot,
  RespondApplyInput,
  SearchedGroup,
  SearchedUser,
} from '~/worker/contract';

export interface ContactsSlice {
  /** null = 未加载（未登录/未进过通讯录页/拉取失败/已失效） */
  readonly book: ContactsBook | null;
  readonly groupChats: readonly ContactGroupChat[] | null;
  readonly points: ContactPoints | null;
}

export const CONTACTS_INITIAL: ContactsSlice = { book: null, groupChats: null, points: null };

export interface ContactsConnection {
  /** 进通讯录页调（幂等；Worker 缓存命中零 HTTP）。false=无数据（空态重试钮） */
  loadBook(): Promise<boolean>;
  /** 切群聊 Tab 调（同上） */
  loadGroupChats(): Promise<boolean>;
  /** 进通讯录页调（红点；同上） */
  loadPoints(): Promise<boolean>;
  /**
   * 手动刷新三件（21ae 通讯录头部刷新钮）：Worker 丢缓存推 null → 切片归 null
   * （页面出加载态）→ 这里紧接着重拉三件（「null 则 load」也会兜底，重复调用
   * 由 Worker 的 inflight 去重合并为一次 HTTP）。false=无 session。
   */
  refresh(): Promise<boolean>;
  /**
   * 服务端搜用户/群（14.19a；透传不进 GlobalState——瞬态结果归搜索页组件态，
   * loadGroupMembers 同口径）。**调用方必须显式触发**（封号级限额，契约注）。
   * null=无 session；reject 带服务端文案（调用方 toast/错误条直出）。
   */
  searchUsers(val: string): Promise<readonly SearchedUser[] | null>;
  searchGroups(val: string): Promise<readonly SearchedGroup[] | null>;
  /** 发好友申请（14.19a）。reject 带服务端文案（重复申请/已是好友等） */
  addFriend(input: AddFriendInput): Promise<boolean>;
  /** 申请入群（14.19a） */
  applyGroup(input: GroupApplyInput): Promise<boolean>;
  /**
   * 读申请列表（14.20；透传不进 GlobalState——瞬态结果归申请页组件态；红点
   * 找平由 Worker 走 'chat/contacts' 推送回流）。reject 带服务端文案。
   */
  loadApplies(kind: ApplyKind): Promise<readonly ContactApply[] | null>;
  /** 审核申请（14.20）。reject 带服务端文案（调用方 toast + 重拉列表找平） */
  respondApply(input: RespondApplyInput): Promise<boolean>;
  /**
   * 好友详情五件（14.21a；透传不进 GlobalState——详情页瞬态；好友册/会话行的
   * 本地找平由 Worker 走 'chat/contacts'/'chat/conversations' 推送回流）。
   * reject 带服务端文案（非好友禁止查看/仅可拉黑好友/记忆超长等，直出给用户）。
   */
  loadFriendProfile(friendId: number): Promise<FriendProfile | null>;
  changeFriend(friendId: number, field: FriendChangeField, value: string): Promise<boolean>;
  setFriendNoDisturb(friendId: number, on: boolean): Promise<boolean>;
  setBlack(userId: number, on: boolean): Promise<boolean>;
  /** 删除好友（双向物理删含聊天记录——调用方必须强确认后再调） */
  removeFriend(friendId: number, userNo: number): Promise<boolean>;
  /** 删除聊天记录（self=仅自己 / both=双方）。false=本地无会话可删（仅 self 档） */
  cleanHistory(key: ConversationKey, scope: 'self' | 'both'): Promise<boolean>;
  /**
   * 设置好友阅后即焚秒数（14.21b 详情页入口；chat/afterView/set 契约本就按
   * friendId，不依赖打开中会话——messagesState.setAfterView 是聊天页火焰徽章
   * 的打开中会话口径，两处同一 RPC）。false=HTTP 失败/无 session。
   */
  setFriendAfterView(friendId: number, second: number): Promise<boolean>;
  /**
   * 好友分组管理四件（14.22a；好友册的本地找平由 Worker 走 'chat/contacts'
   * 推送回流，调用方不需要也不应该重拉 book）。reject 带服务端文案（分组名称
   * 已存在等，直出给用户）；false=入参非法/组不在册/无 session。
   */
  addFriendGroup(name: string): Promise<boolean>;
  renameFriendGroup(groupId: number, name: string): Promise<boolean>;
  deleteFriendGroup(groupId: number): Promise<boolean>;
  moveFriendsToGroup(friendIds: readonly number[], groupId: number): Promise<boolean>;
  /**
   * 读黑名单（14.22a；透传不进 GlobalState——瞬态结果归黑名单页组件态，
   * loadApplies 同口径；刻意不缓存每次进页一拉）。移出用 setBlack(uid,false)。
   */
  loadBlackList(): Promise<readonly BlackListUser[] | null>;
  /**
   * 读系统公告（14.24；透传不进 GlobalState——瞬态结果归公告页组件态，
   * loadBlackList 同口径；刻意不缓存每次进页一拉）。
   */
  loadAnnouncements(): Promise<readonly AnnouncementItem[] | null>;
  /**
   * 建群（14.23a；瞬态透传——成功后会话行/群聊 Tab 走推送回流）。返回群 id
   * （调用方据此 openChat）；null=入参非法/无 session；业务错 reject 带文案。
   */
  createGroup(
    name: string,
    friendIds: readonly number[],
    avatar?: { readonly blob: Blob; readonly filename: string },
  ): Promise<number | null>;
  /**
   * 读群资料快照（14.25a；透传不进 GlobalState——群设置页瞬态，loadFriendProfile
   * 同口径）。Worker 与输入栏限制态共用缓存：打开群聊后进设置页零 HTTP
   * （requests 场景 Z）。null=非群会话/拉取失败（页面重试）。
   */
  loadGroupProfile(key: ConversationKey): Promise<GroupProfileSnapshot | null>;
  /** 会话置顶（14.25a；无 WS 推送，成功后会话行推送回流）。false=无行/占位行 */
  setConversationTop(key: ConversationKey, on: boolean): Promise<boolean>;
  /** 群免打扰（14.25a；setFriendNoDisturb 的群变体） */
  setGroupNoDisturb(groupId: number, on: boolean): Promise<boolean>;
  /**
   * 群管理写链八件（14.26a；瞬态透传——成功后快照/会话行/缓存由 Worker 本地
   * 直改推送回流，调用方不重拉）。业务错 reject 带服务端文案（乐观回滚+toast）。
   */
  changeGroup(groupId: number, field: GroupChangeField, value: string): Promise<boolean>;
  changeGroupAvatar(
    groupId: number,
    avatar: { readonly blob: Blob; readonly filename: string },
  ): Promise<boolean>;
  setGroupStillness(groupId: number, on: boolean): Promise<boolean>;
  /** 返回服务端新 AllowChat（0=允许 1=禁止）；null=非法/无 session */
  toggleGroupPrivateChat(groupId: number): Promise<number | null>;
  setGroupAdmin(groupId: number, userId: number, isAdmin: boolean): Promise<boolean>;
  transferGroup(groupId: number, userId: number): Promise<boolean>;
  /**
   * 读群全量成员（14.26b-2 管理员任免/转让选人；messagesState.loadGroupMembers
   * 是打开中会话口径，两处同一 RPC/Worker TTL 60s 缓存——场景 P；任免/转让成功
   * Worker 已失效缓存，重拉恰 1）。null=失败/非群（sheet 内重试）。
   */
  loadGroupMembers(key: ConversationKey): Promise<readonly GroupMember[] | null>;
  /** 退群/解散（调用方必须强确认后再调；成功后会话退场走推送回流） */
  quitGroup(groupId: number): Promise<boolean>;
  dissolveGroup(groupId: number): Promise<boolean>;
  /**
   * 群成员操作四件 + 进出日志（14.27a；瞬态透传，requests 场景 AB——成功后
   * 成员缓存/快照由 Worker 本地直改，调用方重开面板现读）。业务错 reject 带
   * 服务端文案；禁言是布尔无时长（旧版 6 档假 UI，契约注）。
   */
  muteGroupMember(groupId: number, userId: number, muted: boolean): Promise<boolean>;
  removeGroupMember(groupId: number, userId: number): Promise<boolean>;
  changeGroupMemberNote(groupId: number, userId: number, note: string): Promise<boolean>;
  /** 'joined'=直接入群 / 'pending'=待审核；null=非法/无 session */
  inviteGroupMembers(
    groupId: number,
    userIds: readonly number[],
  ): Promise<InviteMembersResult | null>;
  /** 进出日志一页（MaxId 游标，首页 0；仅群主/管理员，普通成员业务错 reject） */
  loadGroupMemberLog(
    groupId: number,
    maxId: number,
  ): Promise<readonly GroupMemberLogItem[] | null>;
  /** Worker 崩溃重建后重拉已加载过的字段（内存缓存丢失） */
  resync(): void;
  dispose(): void;
}

export function connectContacts(store: GlobalStore, host: AppWorkerHost): ContactsConnection {
  let epoch = 0;
  const loaded = { book: false, groupChats: false, points: false };

  function merge(partial: Partial<ContactsSlice>): void {
    store.set({ contacts: { ...store.get().contacts, ...partial } });
  }

  const offPush = host.onPush('chat/contacts', (push) => {
    const partial: Partial<{ -readonly [K in keyof ContactsSlice]: ContactsSlice[K] }> = {};
    if (push.book !== undefined) partial.book = push.book;
    if (push.groupChats !== undefined) partial.groupChats = push.groupChats;
    if (push.points !== undefined) partial.points = push.points;
    merge(partial);
  });

  const offAuth = store.subscribe(
    (s) => s.auth,
    (auth) => {
      if (auth.status === 'logged-out') {
        epoch += 1; // 在途响应过期
        loaded.book = loaded.groupChats = loaded.points = false;
        store.set({ contacts: CONTACTS_INITIAL });
        return;
      }
      // 登录即拉一次红点（21ag）：申请红点不再只服务通讯录页——导航「通讯录」
      // 项要画红点、会话列表要出「好友申请/群通知」两条申请会话（旧版
      // chat_list_screen 的 _applyConversationChat），这两处在任何 Tab 都可见，
      // 不能等用户进了通讯录页才拉。Worker 侧有会话期缓存 + 9/24 信令本地 +1，
      // 所以整个会话期就这 1 个请求（冷启动预算 A 本就含 point）。
      if (auth.status === 'logged-in' && !auth.needsPassword && !loaded.points) {
        void load('points', 'chat/contacts/points');
      }
    },
  );

  // epoch 只在登出时递增（在途响应作废）；三字段并发加载互不影响
  function load<K extends keyof ContactsSlice>(
    field: K,
    method: 'chat/contacts/book' | 'chat/contacts/groupChats' | 'chat/contacts/points',
  ): Promise<boolean> {
    const seq = epoch;
    loaded[field] = true;
    return host.call(method, null).then(
      (value) => {
        if (seq !== epoch) return false;
        if (value !== null) merge({ [field]: value } as Partial<ContactsSlice>);
        return value !== null;
      },
      () => false,
    );
  }

  return {
    loadBook: () => load('book', 'chat/contacts/book'),
    loadGroupChats: () => load('groupChats', 'chat/contacts/groupChats'),
    loadPoints: () => load('points', 'chat/contacts/points'),
    refresh: async () => {
      const ok = await host.call('chat/contacts/refresh', null).catch(() => false);
      if (!ok) return false;
      await Promise.all([
        load('book', 'chat/contacts/book'),
        load('groupChats', 'chat/contacts/groupChats'),
        load('points', 'chat/contacts/points'),
      ]);
      return true;
    },
    searchUsers: (val) => host.call('chat/search/users', { val }),
    searchGroups: (val) => host.call('chat/search/groups', { val }),
    addFriend: (input) => host.call('chat/friend/add', input),
    applyGroup: (input) => host.call('chat/group/apply', input),
    loadApplies: (kind) => host.call('chat/applies/load', { kind }),
    respondApply: (input) => host.call('chat/applies/respond', input),
    loadFriendProfile: (friendId) => host.call('chat/friend/profile', { friendId }),
    changeFriend: (friendId, field, value) =>
      host.call('chat/friend/change', { friendId, field, value }),
    setFriendNoDisturb: (friendId, on) => host.call('chat/friend/noDisturb', { friendId, on }),
    setBlack: (userId, on) => host.call('chat/friend/black', { userId, on }),
    removeFriend: (friendId, userNo) => host.call('chat/friend/remove', { friendId, userNo }),
    cleanHistory: (key, scope) => host.call('chat/history/clean', { key, scope }),
    setFriendAfterView: (friendId, second) =>
      host.call('chat/afterView/set', { friendId, second }).catch(() => false),
    addFriendGroup: (name) => host.call('chat/friendGroup/add', { name }),
    renameFriendGroup: (groupId, name) => host.call('chat/friendGroup/rename', { groupId, name }),
    deleteFriendGroup: (groupId) => host.call('chat/friendGroup/delete', { groupId }),
    moveFriendsToGroup: (friendIds, groupId) =>
      host.call('chat/friendGroup/move', { friendIds, groupId }),
    loadBlackList: () => host.call('chat/blacklist/load', null),
    loadAnnouncements: () => host.call('chat/announcements/load', null),
    createGroup: (name, friendIds, avatar) =>
      host.call('chat/group/create', { name, friendIds, ...(avatar ? { avatar } : {}) }),
    loadGroupProfile: (key) => host.call('chat/groupProfile/load', { key }),
    setConversationTop: (key, on) => host.call('chat/conversation/top', { key, on }),
    setGroupNoDisturb: (groupId, on) => host.call('chat/group/noDisturb', { groupId, on }),
    changeGroup: (groupId, field, value) => host.call('chat/group/change', { groupId, field, value }),
    changeGroupAvatar: (groupId, avatar) => host.call('chat/group/changeAvatar', { groupId, avatar }),
    setGroupStillness: (groupId, on) => host.call('chat/group/stillness', { groupId, on }),
    toggleGroupPrivateChat: (groupId) => host.call('chat/group/privateChat', { groupId }),
    setGroupAdmin: (groupId, userId, isAdmin) =>
      host.call('chat/group/setAdmin', { groupId, userId, isAdmin }),
    transferGroup: (groupId, userId) => host.call('chat/group/transfer', { groupId, userId }),
    loadGroupMembers: (key) => host.call('chat/members/load', { key }).catch(() => null),
    quitGroup: (groupId) => host.call('chat/group/quit', { groupId }),
    dissolveGroup: (groupId) => host.call('chat/group/dissolve', { groupId }),
    muteGroupMember: (groupId, userId, muted) =>
      host.call('chat/members/mute', { groupId, userId, muted }),
    removeGroupMember: (groupId, userId) => host.call('chat/members/remove', { groupId, userId }),
    changeGroupMemberNote: (groupId, userId, note) =>
      host.call('chat/members/changeNote', { groupId, userId, note }),
    inviteGroupMembers: (groupId, userIds) => host.call('chat/members/invite', { groupId, userIds }),
    loadGroupMemberLog: (groupId, maxId) => host.call('chat/members/log', { groupId, maxId }),
    resync() {
      if (loaded.book) void load('book', 'chat/contacts/book');
      if (loaded.groupChats) void load('groupChats', 'chat/contacts/groupChats');
      if (loaded.points) void load('points', 'chat/contacts/points');
    },
    dispose() {
      offPush();
      offAuth();
    },
  };
}
